import { useState, useEffect, useCallback } from 'react';
import type { SavedScenario, ReverseCalculationInput, ReverseCalculationResult } from '../types/tax';

const STORAGE_KEY = 'reversetax_saved_scenarios';

export function useScenarios() {
  const [scenarios, setScenarios] = useState<SavedScenario[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load scenarios from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert string dates back to Date objects
        const scenariosWithDates = parsed.map((scenario: Omit<SavedScenario, 'createdAt' | 'updatedAt'> & { createdAt: string; updatedAt: string }) => ({
          ...scenario,
          createdAt: new Date(scenario.createdAt),
          updatedAt: new Date(scenario.updatedAt),
        }));
        setScenarios(scenariosWithDates);
      }
      setIsLoaded(true);
    } catch (error) {
      console.error('Error loading scenarios from localStorage:', error);
      setIsLoaded(true);
    }
  }, []);

  // Save scenarios to localStorage whenever scenarios change (but not on initial load)
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(scenarios));
      } catch (error) {
        console.error('Error saving scenarios to localStorage:', error);
      }
    }
  }, [scenarios, isLoaded]);

  // Generate unique ID for new scenarios
  const generateId = useCallback(() => {
    return `scenario_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Save a new scenario
  const saveScenario = useCallback((
    name: string,
    input: ReverseCalculationInput,
    result: ReverseCalculationResult,
    inputFrequency: 'monthly' | 'yearly'
  ) => {
    const newScenario: SavedScenario = {
      id: generateId(),
      name,
      input,
      result,
      inputFrequency,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setScenarios(prev => {
      // Limit to maximum 3 scenarios, remove oldest if needed
      const updated = [newScenario, ...prev];
      return updated.slice(0, 3);
    });

    return newScenario;
  }, [generateId]);

  // Update an existing scenario
  const updateScenario = useCallback((
    id: string,
    updates: Partial<Pick<SavedScenario, 'name' | 'input' | 'result' | 'inputFrequency'>>
  ) => {
    setScenarios(prev => prev.map(scenario => 
      scenario.id === id 
        ? { ...scenario, ...updates, updatedAt: new Date() }
        : scenario
    ));
  }, []);

  // Delete a scenario
  const deleteScenario = useCallback((id: string) => {
    setScenarios(prev => prev.filter(scenario => scenario.id !== id));
  }, []);

  // Delete all scenarios
  const clearAllScenarios = useCallback(() => {
    setScenarios([]);
  }, []);


  // Get scenario by ID
  const getScenario = useCallback((id: string) => {
    return scenarios.find(s => s.id === id) || null;
  }, [scenarios]);

  return {
    scenarios,
    saveScenario,
    updateScenario,
    deleteScenario,
    clearAllScenarios,
    getScenario,
    maxScenarios: 3, // Maximum number of scenarios allowed
  };
}